<template>
  <section :class="['farm-aura-page', 'farm-aura-page--' + theme]">
    <div class="aura-page-toolbar">
      <div>
        <span class="farm-panel-kicker">灵兽园</span>
        <h3>真灵列表</h3>
        <p>查看真灵属性、神通、包裹与永久强化</p>
      </div>
      <div class="aura-page-actions">
        <span class="aura-count">{{ filteredPetsList.length }} / {{ petsList.length }} 只真灵</span>
        <Button size="small" :loading="listLoading" @click="loadPets">刷新</Button>
        <Button size="small" type="primary" @click="travelVisible = true">真灵游历</Button>
      </div>
    </div>

    <div class="aura-garden-content">
      <div class="aura-filter-panel">
      <div class="aura-filter-controls">
        <Input v-model.trim="auraFilters.keyword" clearable size="small" placeholder="搜索真灵名/种族" />
        <Select v-model="auraFilters.type" size="small" placeholder="类型">
          <Option :value="0">全部类型</Option>
          <Option v-for="option in auraTypeOptions" :key="'type-' + option.value" :value="option.value">{{ option.label }}</Option>
        </Select>
        <Select v-model="auraFilters.element" size="small" placeholder="元素">
          <Option :value="0">全部元素</Option>
          <Option v-for="option in auraElementOptions" :key="'element-' + option.value" :value="option.value">{{ option.label }}</Option>
        </Select>
        <Select v-model="auraFilters.grade" size="small" placeholder="品级">
          <Option :value="0">全部品级</Option>
          <Option v-for="option in auraGradeOptions" :key="'grade-' + option.value" :value="option.value">{{ option.label }}</Option>
        </Select>
        <Select v-model="auraFilters.aptitude" size="small" placeholder="资质">
          <Option value="">全部资质</Option>
          <Option v-for="option in auraAptitudeOptions" :key="'aptitude-' + option.value" :value="option.value">{{ option.label }}</Option>
        </Select>
        <Select v-model="auraFilters.status" size="small" placeholder="状态">
          <Option :value="-1">全部状态</Option>
          <Option :value="1">出战中</Option>
          <Option :value="0">休息中</Option>
        </Select>
        <Select v-model="auraFilters.sortKey" size="small" placeholder="排序字段">
          <Option v-for="option in auraSortOptions" :key="'sort-' + option.value" :value="option.value">{{ option.label }}</Option>
        </Select>
        <RadioGroup v-model="auraFilters.sortOrder" type="button" size="small">
          <Radio label="desc">降序</Radio>
          <Radio label="asc">升序</Radio>
        </RadioGroup>
        <Button size="small" @click="resetAuraFilters">重置</Button>
        <Button size="small" type="error" @click="batchReleaseConfirm">放生全部</Button>
      </div>
      </div>

      <div class="aura-layout">
      <aside class="aura-left">
        <div class="aura-list">
          <button v-for="item in filteredPetsList" :key="item.auraId" type="button" class="aura-list-item" :class="{ active: item.auraId === selectedAuraId }" @click="selectAura(item)">
            <img :src="item.auraIcon" alt="真灵头像" class="aura-list-icon" />
            <Tag class="aura-card-grade" size="small" :color="getGradeColor(item.auraGrade)">{{ getGradeName(item.auraGrade) }}</Tag>
            <button type="button" class="aura-card-follow" :class="{ active: Number(item.auraFollowed || 0) === 1 }" :aria-label="Number(item.auraFollowed || 0) === 1 ? '取消关注' : '关注'" @click.stop="toggleAuraFollow(item)"><Icon :type="Number(item.auraFollowed || 0) === 1 ? 'ios-heart' : 'ios-heart-outline'" /></button>
            <strong class="aura-card-name">{{ item.auraName || '未命名真灵' }}</strong>
            <small class="aura-card-status">Lv.{{ item.auraLevel || 0 }} · {{ Number(item.auraStatus) === 1 ? '出战中' : '休息中' }}</small>
          </button>
          <div v-if="listLoading" class="aura-list-empty">正在读取真灵列表...</div>
          <div v-else-if="!filteredPetsList.length" class="aura-list-empty">没有符合筛选条件的真灵</div>
        </div>
      </aside>

      <main class="aura-right">
        <div v-if="selectedAura" class="aura-detail">
          <div class="aura-detail-head">
            <div class="aura-basic-info">
              <div class="aura-name-line">
                <h3>{{ selectedAura.auraName || '未命名真灵' }}</h3>
                <Tag :color="getGradeColor(selectedAura.auraGrade)">{{ getGradeName(selectedAura.auraGrade) }}</Tag>
                <Tag v-if="Number(selectedAura.auraStatus) === 1" color="red">出战中</Tag>
                <button type="button" class="detail-follow" :class="{ active: Number(selectedAura.auraFollowed || 0) === 1 }" :aria-label="Number(selectedAura.auraFollowed || 0) === 1 ? '取消关注' : '关注'" @click="toggleAuraFollow(selectedAura)"><Icon :type="Number(selectedAura.auraFollowed || 0) === 1 ? 'ios-heart' : 'ios-heart-outline'" /></button>
              </div>
              <div class="aura-subline">{{ selectedAura.auraRace || '未知种族' }} · {{ getElementName(selectedAura.auraElement) }} · {{ getTypeName(selectedAura.auraType) }} · 战力 {{ formatCombatPower(selectedAura.auraCombot) }}</div>
              <div class="aura-fact-line"><span>资质 <b :style="getAptitudeStyle(selectedAura.auraAptitude)">{{ selectedAura.auraAptitude || 0 }}</b></span><span>等级上限 <b>{{ selectedAura.auraLevelCap || 0 }}</b></span><span>饱食度 <b>{{ selectedAura.auraStarvationValue || 0 }}</b></span></div>
              <div class="aura-exp-line"><span>修为 {{ selectedAura.auraExp || 0 }} / {{ selectedAura.auraUpgradeExp || 0 }}</span><Progress :percent="auraExpPercent" :stroke-width="8" hide-info /></div>
              <div class="aura-action-row">
                <Button size="small" type="primary" @click="goToWar(selectedAura)">{{ Number(selectedAura.auraStatus) === 1 ? '休息' : '出战' }}</Button>
                <Button size="small" @click="openRename">改名</Button>
                <Button size="small" type="error" @click="releaseConfirm">放生</Button>
                <Button size="small" @click="openLearnSkill">学习神通</Button>
                <Button size="small" @click="openReplaceSkill">替换被动</Button>
                <Button size="small" type="warning" @click="openAwakening">真灵觉醒</Button>
              </div>
            </div>
          </div>

          <Tabs v-model="detailTab" class="aura-detail-tabs" :animated="false">
            <TabPane label="属性·神通" name="attr">
              <div class="aura-story" v-if="selectedAura.auraDesci"><span>背景故事</span><p>{{ selectedAura.auraDesci }}</p></div>
              <div class="aura-section-title">基础属性</div>
              <div class="aura-attributes-grid">
                <div v-for="attr in auraAttributeRows" :key="attr.key" class="aura-attribute"><span>{{ attr.label }}</span><strong>{{ attr.value }}</strong></div>
              </div>
              <div class="aura-section-title aura-section-title--skill">神通</div>
              <div class="aura-skills-columns">
                <div class="aura-skill-column"><h4>主动神通</h4><button v-for="skill in activeAuraSkills" :key="'active-' + getAuraSkillKey(skill)" type="button" class="aura-skill" :class="{ active: isSelectedAuraSkill(skill) }" @click="selectAuraSkill(skill)">{{ skill.skillName }}</button><span v-if="!activeAuraSkills.length" class="aura-empty">暂无主动神通</span></div>
                <div class="aura-skill-column"><h4>被动 / 光环</h4><button v-for="skill in passiveAuraSkills" :key="'passive-' + getAuraSkillKey(skill)" type="button" class="aura-skill" :class="{ active: isSelectedAuraSkill(skill) }" @click="selectAuraSkill(skill)"><span>{{ skill.skillName }}</span><em>{{ getAuraPassiveTypeLabel(skill) }}</em></button><span v-if="!passiveAuraSkills.length" class="aura-empty">暂无被动神通</span></div>
              </div>
              <div v-if="currentAuraSkill" class="aura-skill-detail"><div><strong>{{ currentAuraSkill.skillName }}</strong><Tag size="small" :color="isAuraPassiveSkill(currentAuraSkill) ? 'default' : 'primary'">{{ isAuraPassiveSkill(currentAuraSkill) ? getAuraPassiveTypeLabel(currentAuraSkill) : '主动' }}</Tag></div><p>{{ currentAuraSkill.skillDesc || '暂无技能描述' }}</p><small v-if="currentAuraSkill.conMana || currentAuraSkill.conMana === 0">真气消耗：{{ currentAuraSkill.conMana }}</small></div>
              <div v-else class="aura-empty aura-skill-detail">暂无神通</div>
            </TabPane>
            <TabPane label="强化" name="enhance">
              <div class="aura-tab-toolbar"><span>永久属性强化</span><Button size="small" :loading="permanentLoading" @click="loadAuraPermanentAttrInfo">刷新</Button></div>
              <div v-if="permanentLoading" class="aura-empty">正在加载永久强化信息...</div>
              <template v-else>
                <div v-if="auraPermanentAttrConfigs.length" class="aura-enhance-grid"><div v-for="config in auraPermanentAttrConfigs" :key="'config-' + config.itemId" class="aura-enhance-card" :class="{ disabled: !canUseAuraPermanentAttr(config) }"><div class="aura-enhance-head"><strong>{{ config.itemName }}</strong><Tag>{{ config.attrName }}</Tag></div><p>每次增加 {{ formatAuraPermanentAttrDelta(config.attrType, config.delta) }}</p><small>拥有 {{ config.ownCount || 0 }} · 已用 {{ config.useCount || 0 }} / {{ config.maxCount || 0 }} · 累计 {{ formatAuraPermanentAttrDelta(config.attrType, config.totalValue || 0) }}</small><Button long size="small" type="primary" :disabled="!canUseAuraPermanentAttr(config)" :loading="usingPermanentItemId === config.itemId" @click="useAuraPermanentAttrMaterial(config)">炼化强化</Button></div></div>
                <div v-else class="aura-empty">暂无可用永久强化材料配置</div>
                <div v-if="auraPermanentAttrRecords.length" class="aura-records"><h4>当前真灵累计强化</h4><div v-for="record in auraPermanentAttrRecords" :key="'record-' + record.itemId" class="aura-record-row"><span>{{ record.attrName }}</span><strong>{{ formatAuraPermanentAttrDelta(record.attrType, record.totalValue || 0) }}</strong><small>使用 {{ record.useCount || 0 }} 次</small></div></div>
              </template>
            </TabPane>
          </Tabs>
        </div>
        <div v-else class="aura-no-selection">请从左侧选择一个真灵查看详情</div>
      </main>
      </div>
    </div>

    <Modal v-model="renameVisible" title="真灵改名" @on-ok="renameAura"><Input v-model.trim="renameValue" :maxlength="12" placeholder="请输入新的真灵名称" /></Modal>
    <Modal v-model="learnVisible" title="学习主动神通" @on-ok="learnZhenSkill"><Select v-model="learnPackItemId" clearable placeholder="选择真灵书"><Option v-for="item in learnSkillBooks" :key="item.packItemId" :value="item.packItemId">{{ item.itemName || item.name || '真灵神通书' }} × {{ item.itemNum || 1 }}</Option></Select></Modal>
    <Modal v-model="replaceVisible" title="替换被动神通" @on-ok="replacePassiveSkill"><Select v-model="replacePackItemId" clearable placeholder="选择真灵书"><Option v-for="item in replaceSkillBooks" :key="item.packItemId" :value="item.packItemId">{{ item.itemName || item.name || '真灵被动书' }} × {{ item.itemNum || 1 }}</Option></Select></Modal>
    <Modal v-model="releaseVisible" title="确认放生" @on-ok="releaseAura"><p>确认放生当前真灵吗？此操作不可撤销。</p></Modal>
    <Modal v-model="batchReleaseVisible" title="批量放生" @on-ok="batchRelease"><p>确认放生当前筛选出的 {{ filteredPetsList.length }} 只真灵吗？出战中或已关注的真灵不会被放生。</p></Modal>
    <Modal v-model="awakeningVisible" title="真灵觉醒" @on-ok="completeAwakening"><div v-if="awakeningInfo" class="aura-awakening-info"><p>{{ awakeningInfo.description || '真灵达到等级上限后才可觉醒。觉醒会继承被动神通，主动神通不保留；觉醒材料来自真灵游历。' }}</p><div v-for="(value, key) in awakeningInfo" v-if="!['description', 'msg'].includes(key) && (typeof value !== 'object')" :key="key"><span>{{ key }}</span><strong>{{ value }}</strong></div></div><div v-else>正在读取觉醒条件...</div></Modal>
    <Modal v-model="travelVisible" title="" width="1200" class-name="aura-travel-modal" :footer-hide="true">
      <petMap v-if="travelVisible" :farm-mode="true" />
    </Modal>
  </section>
</template>

<script>
import petMap from '../petMap.vue'

export default {
  name: 'CaveFarmAura',
  props: { theme: { type: String, default: 'blue' } },
  components: { petMap },
  data () {
    return {
      petsList: [],
      travelVisible: false,
      selectedAuraId: null,
      selectedAura: null,
      listLoading: false,
      detailTab: 'attr',
      selectedAuraSkillKey: null,
      auraFilters: { keyword: '', type: 0, element: 0, grade: 0, aptitude: '', status: -1, sortKey: 'combat', sortOrder: 'desc' },
      auraTypeOptions: [{ value: 1, label: '辅助' }, { value: 2, label: '输出' }, { value: 3, label: '防御' }],
      auraElementOptions: [{ value: 1, label: '金' }, { value: 2, label: '木' }, { value: 3, label: '水' }, { value: 4, label: '火' }, { value: 5, label: '土' }, { value: 6, label: '风' }, { value: 7, label: '雷' }, { value: 8, label: '冰' }],
      auraGradeOptions: [{ value: 1, label: '普通' }, { value: 2, label: '稀有' }, { value: 3, label: '灵级' }, { value: 4, label: '王级' }, { value: 5, label: '域级' }, { value: 6, label: '圣级' }, { value: 7, label: '神级' }],
      auraAptitudeOptions: [{ value: '10-', label: '10以下' }, { value: '9-', label: '9以下' }, { value: '8-', label: '8以下' }, { value: '7-', label: '7以下' }, { value: '6-', label: '6以下' }],
      auraSortOptions: [{ value: 'combat', label: '按战力' }, { value: 'level', label: '按等级' }, { value: 'aptitude', label: '按资质' }, { value: 'grade', label: '按品级' }, { value: 'starvation', label: '按体力' }, { value: 'life', label: '按生命' }, { value: 'attack', label: '按力攻' }, { value: 'magicAttack', label: '按灵攻' }, { value: 'speed', label: '按速度' }],
      auraPermanentAttrInfo: null,
      auraPermanentMaterialBag: [],
      permanentLoading: false,
      usingPermanentItemId: null,
      renameVisible: false,
      renameValue: '',
      learnVisible: false,
      learnPackItemId: '',
      replaceVisible: false,
      replacePackItemId: '',
      releaseVisible: false,
      batchReleaseVisible: false,
      awakeningVisible: false,
      awakeningInfo: null
    }
  },
  computed: {
    filteredPetsList () {
      const filters = this.auraFilters
      let list = this.petsList.slice()
      const keyword = (filters.keyword || '').trim().toLowerCase()
      if (keyword) list = list.filter(item => [item.auraName, item.auraRace, this.getElementName(item.auraElement), this.getTypeName(item.auraType), this.getGradeName(item.auraGrade)].some(value => String(value || '').toLowerCase().includes(keyword)))
      if (Number(filters.type) > 0) list = list.filter(item => Number(item.auraType) === Number(filters.type))
      if (Number(filters.element) > 0) list = list.filter(item => Number(item.auraElement) === Number(filters.element))
      if (Number(filters.grade) > 0) list = list.filter(item => Number(item.auraGrade) === Number(filters.grade))
      if (Number(filters.status) >= 0) list = list.filter(item => Number(item.auraStatus) === Number(filters.status))
      if (filters.aptitude) list = list.filter(item => this.matchAuraAptitude(item.auraAptitude, filters.aptitude))
      const direction = filters.sortOrder === 'asc' ? 1 : -1
      list.sort((a, b) => {
        const followedDiff = Number(b.auraFollowed || 0) - Number(a.auraFollowed || 0)
        if (followedDiff) return followedDiff
        const valueA = this.getAuraSortValue(a, filters.sortKey)
        const valueB = this.getAuraSortValue(b, filters.sortKey)
        return valueA === valueB ? Number(a.auraId || 0) - Number(b.auraId || 0) : (valueA > valueB ? direction : -direction)
      })
      return list
    },
    activeAuraSkills () { return this.getAuraSkillsByType('active') },
    passiveAuraSkills () { return this.getAuraSkillsByType('passive') },
    currentAuraSkill () { const list = this.selectedAura && Array.isArray(this.selectedAura.gamePetSKillList) ? this.selectedAura.gamePetSKillList : []; return list.find(item => this.getAuraSkillKey(item) === this.selectedAuraSkillKey) || list[0] || null },
    auraPermanentAttrConfigs () { return this.auraPermanentAttrInfo && Array.isArray(this.auraPermanentAttrInfo.configs) ? this.auraPermanentAttrInfo.configs : [] },
    auraPermanentAttrRecords () { return this.auraPermanentAttrInfo && Array.isArray(this.auraPermanentAttrInfo.records) ? this.auraPermanentAttrInfo.records : [] },
    auraExpPercent () { const current = Number(this.selectedAura && this.selectedAura.auraExp || 0); const max = Number(this.selectedAura && this.selectedAura.auraUpgradeExp || 0); return max > 0 ? Math.min(100, Math.max(0, current / max * 100)) : 0 },
    auraAttributeRows () {
      const aura = this.selectedAura || {}
      return [
        ['auraMagicAttack', '灵力攻击'], ['auraAttack', '力道攻击'], ['auraLife', '生命值'], ['auraMana', '真气'],
        ['auraDefence', '物理防御'], ['auraMagicDefence', '法术防御'], ['auraPhysique', '根骨'], ['auraDexterity', '身法'],
        ['auraSpirit', '神识'], ['auraActionSpeed', '速度'], ['auraHitRate', '命中'], ['auraEvade', '闪避'],
        ['auraExplode', '暴击', true], ['auraCalDamage', '暴击伤害', true], ['auraLiDaoReductionRate', '力道减伤', true],
        ['auraLingLiReductionRate', '灵力减伤', true], ['damageBoost', '伤害增幅', true], ['damageDeduct', '伤害减免', true]
      ].map(item => ({ key: item[0], label: item[1], value: this.formatAuraValue(aura[item[0]], item[2]) }))
    },
    learnSkillBooks () { return this.buildUniquePetSkillBookList(this.$store.state.shenSkillList, [20]) },
    replaceSkillBooks () { return this.buildUniquePetSkillBookList(this.$store.state.shenSkillList, [17]) }
  },
  watch: {
    filteredPetsList () { this.syncSelectedAuraToVisibleList() },
    selectedAuraId () { this.loadAuraPermanentAttrInfo() }
  },
  mounted () { this.loadPets() },
  methods: {
    getCookie (name) { const value = '; ' + document.cookie; const parts = value.split('; ' + name + '='); return parts.length === 2 ? parts.pop().split(';').shift() : '' },
    async loadPets () {
      this.listLoading = true
      try {
        const res = await this.$http.post('/gameAura/getCharaPet?charaId=' + this.getCookie('charaId'))
        if (res.data && res.data.status === 200) { this.petsList = Array.isArray(res.data.data) ? res.data.data : []; this.syncSelectedAuraToVisibleList() } else this.$Message.warning((res.data && res.data.msg) || '获取真灵列表失败')
      } catch (error) { this.$Message.warning('获取真灵列表失败') } finally { this.listLoading = false }
    },
    selectAura (item) { this.selectedAuraId = item.auraId; this.selectedAura = item; this.detailTab = 'attr'; this.syncSelectedAuraSkill() },
    syncSelectedAuraToVisibleList () {
      const list = this.filteredPetsList
      if (!list.length) { this.selectedAuraId = null; this.selectedAura = null; return }
      const current = list.find(item => String(item.auraId) === String(this.selectedAuraId)) || list[0]
      this.selectedAuraId = current.auraId
      this.selectedAura = this.petsList.find(item => String(item.auraId) === String(current.auraId)) || current
      this.syncSelectedAuraSkill()
    },
    syncSelectedAuraSkill () { const list = this.selectedAura && Array.isArray(this.selectedAura.gamePetSKillList) ? this.selectedAura.gamePetSKillList : []; if (!list.some(item => this.getAuraSkillKey(item) === this.selectedAuraSkillKey)) this.selectedAuraSkillKey = list.length ? this.getAuraSkillKey(list[0]) : null },
    resetAuraFilters () { this.auraFilters = { keyword: '', type: 0, element: 0, grade: 0, aptitude: '', status: -1, sortKey: 'combat', sortOrder: 'desc' } },
    matchAuraAptitude (aptitude, rule) { const value = Number(aptitude); const max = Number(String(rule).replace('-', '')); return Number.isFinite(value) && value < max },
    getAuraSortValue (item, key) { const map = { combat: 'auraCombot', level: 'auraLevel', aptitude: 'auraAptitude', grade: 'auraGrade', starvation: 'auraStarvationValue', life: 'auraLife', attack: 'auraAttack', magicAttack: 'auraMagicAttack', speed: 'auraActionSpeed' }; const value = Number(item && item[map[key] || map.combat]); return Number.isFinite(value) ? value : 0 },
    getGradeName (grade) { return ({ 1: '普通', 2: '稀有', 3: '灵级', 4: '王级', 5: '域级', 6: '圣级', 7: '神级' })[grade] || '未知' },
    getGradeColor (grade) { return ({ 1: 'green', 2: 'blue', 3: 'purple', 4: 'red', 5: 'orange', 6: 'cyan' })[grade] || 'default' },
    getTypeName (type) { return ({ 1: '辅助', 2: '输出', 3: '防御' })[type] || '未知' },
    getElementName (element) { return ({ 1: '金', 2: '木', 3: '水', 4: '火', 5: '土', 6: '风', 7: '雷', 8: '冰' })[element] || '未知' },
    getElementColor (element) { return ({ 1: '#d9a441', 2: '#54a85c', 3: '#3a8ee6', 4: '#d9574d', 5: '#c99036', 6: '#5caa9c', 7: '#8061b1', 8: '#76a6c5' })[element] || '#87909c' },
    getAptitudeStyle (aptitude) { const value = Number(aptitude); if (value >= 10) return { color: '#b27b1e', fontWeight: '700' }; if (value >= 8) return { color: '#8a65a8', fontWeight: '700' }; if (value >= 6) return { color: '#4d8c64', fontWeight: '700' }; return { color: '#8c94a4' } },
    formatCombatPower (power) { const value = Number(power || 0); return value < 10000 ? String(value) : value < 100000000 ? (value / 10000).toFixed(1) + '万' : (value / 100000000).toFixed(1) + '亿' },
    formatAuraValue (value, percent) { const num = Number(value || 0); if (!Number.isFinite(num)) return '0'; return percent ? (num * 100).toFixed(1) + '%' : Number.isInteger(num) ? String(num) : num.toFixed(2) },
    toggleAuraFollow (item) { const followed = Number(item.auraFollowed || 0) === 1 ? 0 : 1; this.$http.post('/gameAura/toggleAuraFollow?charaId=' + this.getCookie('charaId') + '&petId=' + item.auraId + '&followed=' + followed).then(res => { this.$Message.success((res.data && (res.data.data || res.data.msg)) || '操作成功'); this.loadPets() }).catch(() => this.$Message.warning('关注操作失败')) },
    goToWar (item) { const status = Number(item.auraStatus) === 1 ? 0 : 1; this.$http.post('/gameAura/goToWarByAure?charaId=' + this.getCookie('charaId') + '&petId=' + item.auraId + '&aureStatus=' + status).then(res => { this.$Message.success((res.data && (res.data.data || res.data.msg)) || '操作成功'); this.loadPets(); this.$emit('refresh') }).catch(() => this.$Message.warning('出战状态切换失败')) },
    openRename () { this.renameValue = ''; this.renameVisible = true },
    renameAura () { if (!this.renameValue || !this.selectedAuraId) return this.$Message.warning('请输入真灵名称'); this.$http.post('/gameAura/goSpiritRename?charaId=' + this.getCookie('charaId') + '&petId=' + this.selectedAuraId + '&petName=' + encodeURIComponent(this.renameValue)).then(res => { this.$Message.success(res.data.data || res.data.msg || '改名成功'); this.loadPets() }).catch(() => this.$Message.warning('改名失败')) },
    releaseConfirm () { this.releaseVisible = true },
    releaseAura () { if (!this.selectedAura) return; if (Number(this.selectedAura.auraStatus) === 1) return this.$Message.warning('真灵出战中，不能放生'); if (Number(this.selectedAura.auraFollowed || 0) === 1) return this.$Message.warning('已关注真灵不能放生，请先取消关注'); this.$http.post('/gameAura/goSpiritRelease?charaId=' + this.getCookie('charaId') + '&petId=' + this.selectedAura.auraId).then(res => { this.$Message.success(res.data.data || res.data.msg || '放生成功'); this.selectedAuraId = null; this.loadPets() }).catch(() => this.$Message.warning('放生失败')) },
    batchReleaseConfirm () { if (!this.filteredPetsList.length) return this.$Message.warning('当前没有可放生的真灵'); this.batchReleaseVisible = true },
    batchRelease () { const ids = this.filteredPetsList.filter(item => Number(item.auraStatus) !== 1 && Number(item.auraFollowed || 0) !== 1).map(item => item.auraId); if (!ids.length) return this.$Message.warning('当前筛选的真灵均为出战中或已关注，无法放生'); this.$http.post('/gameAura/batchSpiritRelease?charaId=' + this.getCookie('charaId'), ids).then(res => { const result = res.data.data || {}; this.$Message.success('成功放生 ' + (result.successCount || 0) + ' 只真灵'); this.loadPets() }).catch(() => this.$Message.warning('批量放生失败')) },
    getAuraSkillsByType (type) { const list = this.selectedAura && Array.isArray(this.selectedAura.gamePetSKillList) ? this.selectedAura.gamePetSKillList : []; return list.filter(item => type === 'passive' ? this.isAuraPassiveSkill(item) : !this.isAuraPassiveSkill(item)) },
    isAuraPassiveSkill (skill) { const type = String(skill && skill.skillType || ''); return type !== '' && type !== '1' },
    getAuraPassiveTypeLabel (skill) { return String(skill && skill.skillType || '') === '3' ? '光环' : '被动' },
    getAuraSkillKey (skill) { return skill && (skill.skillId || skill.id || skill.skillName) },
    isSelectedAuraSkill (skill) { return this.getAuraSkillKey(skill) === this.selectedAuraSkillKey },
    selectAuraSkill (skill) { this.selectedAuraSkillKey = this.getAuraSkillKey(skill) },
    buildUniquePetSkillBookList (sourceList, allowTypes) { const seen = {}; return (Array.isArray(sourceList) ? sourceList : []).filter(item => allowTypes.includes(Number(item && item.itemType)) && item.packItemId && !seen[item.packItemId] && (seen[item.packItemId] = true)) },
    openLearnSkill () { this.learnPackItemId = ''; this.learnVisible = true },
    openReplaceSkill () { this.replacePackItemId = ''; this.replaceVisible = true },
    learnZhenSkill () { if (!this.learnPackItemId) return this.$Message.warning('请选择真灵书'); this.$http.post('/gameAura/learnZhenSkill?charaId=' + this.getCookie('charaId') + '&petId=' + this.selectedAuraId + '&packItemId=' + this.learnPackItemId).then(res => { this.$Message.success(res.data.data || res.data.msg || '学习成功'); this.loadPets(); this.$emit('refresh') }).catch(() => this.$Message.warning('学习神通失败')) },
    replacePassiveSkill () { if (!this.replacePackItemId) return this.$Message.warning('请选择真灵书'); this.$http.post('/gameAura/replacePassiveZhenSkill?charaId=' + this.getCookie('charaId') + '&petId=' + this.selectedAuraId + '&packItemId=' + this.replacePackItemId).then(res => { this.$Message.success(res.data.data || res.data.msg || '替换成功'); this.loadPets(); this.$emit('refresh') }).catch(() => this.$Message.warning('替换被动神通失败')) },
    async loadAuraPermanentAttrInfo () { if (!this.selectedAuraId) return; this.permanentLoading = true; try { const result = await Promise.all([this.$http.post('/gameAura/getAuraPermanentAttrInfo?charaId=' + this.getCookie('charaId') + '&petId=' + this.selectedAuraId), this.$http.post('/gameChara/getCharaMaterial/?charaId=' + this.getCookie('charaId'))]); this.auraPermanentAttrInfo = result[0].data && result[0].data.status === 200 ? result[0].data.data || {} : null; this.auraPermanentMaterialBag = result[1].data && Array.isArray(result[1].data.data) ? result[1].data.data.filter(item => Number(item.itemType) === 21) : [] } catch (error) { this.auraPermanentAttrInfo = null; this.auraPermanentMaterialBag = []; this.$Message.warning('获取永久强化信息失败') } finally { this.permanentLoading = false } },
    getAuraPermanentAttrPackItemId (itemId) { const source = this.auraPermanentMaterialBag.length ? this.auraPermanentMaterialBag : ((this.$store.state && this.$store.state.materialList) || []); const item = source.find(value => String(value.itemId) === String(itemId) && Number(value.itemNum) > 0); return item ? item.packItemId : '' },
    canUseAuraPermanentAttr (config) { return Number(config && config.ownCount || 0) > 0 && Number(config && config.useCount || 0) < Number(config && config.maxCount || 0) && !!this.getAuraPermanentAttrPackItemId(config.itemId) },
    formatAuraPermanentAttrDelta (attrType, value) { const num = Number(value || 0); if (['CRIT_RATE', 'CRIT_DAMAGE', 'SPEED', 'ELEMENT_BONUS'].includes(String(attrType || ''))) return (num * 100).toFixed(1) + '%'; return Number.isInteger(num) ? String(num) : num.toFixed(2) },
    useAuraPermanentAttrMaterial (config) { const packItemId = this.getAuraPermanentAttrPackItemId(config.itemId); if (!this.canUseAuraPermanentAttr(config) || !packItemId) return this.$Message.warning('当前材料不可使用'); this.usingPermanentItemId = config.itemId; this.$http.post('/gameAura/useAuraPermanentAttrMaterial?charaId=' + this.getCookie('charaId') + '&petId=' + this.selectedAuraId + '&packItemId=' + packItemId).then(res => { this.$Message.success(res.data.data ? ((res.data.data.attrName || config.attrName || '属性') + ' +' + this.formatAuraPermanentAttrDelta(res.data.data.attrType || config.attrType, res.data.data.delta || config.delta)) : '强化成功'); this.loadPets(); this.loadAuraPermanentAttrInfo(); this.$emit('refresh') }).catch(() => this.$Message.warning('强化失败')).finally(() => { this.usingPermanentItemId = null }) },
    async openAwakening () { this.awakeningVisible = true; this.awakeningInfo = null; try { const res = await this.$http.post('/gameAura/getLingCaiAwaken?charaId=' + this.getCookie('charaId') + '&petId=' + this.selectedAuraId); this.awakeningInfo = res.data && res.data.data ? res.data.data : {} } catch (error) { this.$Message.warning('获取觉醒条件失败') } },
    completeAwakening () { if (!this.selectedAuraId) return; this.$http.post('/gameAura/completeSoulAwakening?charaId=' + this.getCookie('charaId') + '&petId=' + this.selectedAuraId).then(res => { this.$Message.success(res.data.msg || res.data.data || '觉醒成功'); this.loadPets(); this.$emit('refresh') }).catch(() => this.$Message.warning('觉醒失败')) }
  }
}
</script>

<style scoped>
.farm-aura-page { color: #315a7c; }
.aura-page-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 14px; padding: 12px 14px; background: #ffffff; border-bottom: 1px solid #c9dced; }
.aura-page-toolbar h3 { margin: 2px 0 3px; color: #4e3823; font: 900 20px/1.2 "KaiTi", "STKaiti", serif; }
.aura-page-toolbar p { margin: 0; color: #9a8462; font-size: 11px; }
.farm-panel-kicker { color: #aa7a35; font-size: 10px; letter-spacing: 1px; }
.aura-page-actions { display: flex; align-items: center; gap: 10px; white-space: nowrap; }
.aura-count { color: #8b6a3e; font-size: 12px; }
.aura-filter-panel { padding: 10px 12px; background: #ffffff; border-bottom: 1px solid #c9dced; }
.aura-filter-controls { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; }
.aura-filter-controls > .ivu-input-wrapper { flex: 1 1 170px; min-width: 150px; }
.aura-filter-controls > .ivu-select { flex: 0 1 112px; min-width: 92px; }
.aura-filter-controls > .ivu-radio-group { flex: 0 0 auto; }
.aura-filter-controls >>> .ivu-input,
.aura-filter-controls >>> .ivu-select-selection,
.aura-filter-controls >>> .ivu-radio-group-button .ivu-radio-wrapper,
.aura-filter-controls >>> .ivu-btn { color: #426581; background: #f7fbff; border-color: #b9cde0; box-shadow: none; }
.aura-filter-controls >>> .ivu-input:hover,
.aura-filter-controls >>> .ivu-input:focus,
.aura-filter-controls >>> .ivu-select-selection:hover,
.aura-filter-controls >>> .ivu-radio-group-button .ivu-radio-wrapper:hover,
.aura-filter-controls >>> .ivu-btn:hover { border-color: #7fa9ca; background: #eef6fd; }
.aura-filter-controls >>> .ivu-input:focus { box-shadow: 0 0 0 2px rgba(181, 138, 82, .16); }
.aura-filter-controls >>> .ivu-input::placeholder { color: #aa916f; }
.aura-filter-controls >>> .ivu-select-arrow,
.aura-filter-controls >>> .ivu-input-icon { color: #98784e; }
.aura-filter-controls >>> .ivu-radio-group-button .ivu-radio-wrapper-checked,
.aura-filter-controls >>> .ivu-radio-group-button .ivu-radio-wrapper-checked:hover { color: #fff8df; background: #6b8f70; border-color: #54765b; box-shadow: none; }
.aura-filter-controls >>> .ivu-btn { height: 30px; padding: 0 10px; font-size: 11px; }
.aura-filter-controls >>> .ivu-btn-error,
.aura-filter-controls >>> .ivu-btn-error:hover { color: #fff7e3; background: #a66c4d; border-color: #875338; }
.aura-filter-controls >>> .ivu-btn:active { box-shadow: inset 0 1px 3px rgba(74, 51, 25, .18); }
.aura-layout { display: grid; grid-template-columns: minmax(0, 7fr) minmax(260px, 3fr); min-height: 590px; background: #f5f9fc; }
.aura-left { min-width: 0; padding: 12px; border-right: 1px solid #c9dced; overflow: hidden; }
.aura-filter-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 6px; margin-top: 7px; }
.aura-filter-actions { display: flex; align-items: center; justify-content: space-between; gap: 5px; margin-top: 7px; }
.aura-filter-actions >>> .ivu-radio-group-button .ivu-radio-wrapper { padding: 0 7px; }
.aura-list { display: grid; grid-template-columns: repeat(auto-fill, minmax(108px, 1fr)); align-content: start; gap: 8px; max-height: 550px; overflow-y: auto; }
.aura-list, .aura-right { scrollbar-width: thin; scrollbar-color: #b58a52 rgba(224, 207, 171, .36); }
.aura-list::-webkit-scrollbar, .aura-right::-webkit-scrollbar { width: 8px; height: 8px; }
.aura-list::-webkit-scrollbar-track, .aura-right::-webkit-scrollbar-track { background: rgba(224, 207, 171, .36); border-radius: 4px; }
.aura-list::-webkit-scrollbar-thumb, .aura-right::-webkit-scrollbar-thumb { background: #b58a52; border: 2px solid rgba(224, 207, 171, .36); border-radius: 4px; }
.aura-list::-webkit-scrollbar-thumb:hover, .aura-right::-webkit-scrollbar-thumb:hover { background: #956b38; }
.aura-list-item { position: relative; display: flex; flex-direction: column; align-items: center; width: 100%; min-height: 142px; padding: 24px 6px 7px; text-align: center; color: #6b5234; background: rgba(255, 253, 245, .82); border: 1px solid #e0d1b4; border-radius: 4px; cursor: pointer; transition: border-color .2s, background .2s, transform .2s; }
.aura-list-item:hover, .aura-list-item.active { background: #eef6fd; border-color: #7fa9ca; }
.aura-list-icon { width: 78px; height: 78px; object-fit: cover; object-position: center; border: 2px solid #9fc1d9; border-radius: 50%; background: #eaf3f9; box-shadow: 0 2px 7px rgba(66, 101, 129, .16), inset 0 0 0 2px rgba(255, 255, 255, .7); }
.aura-card-grade { position: absolute; top: 5px; left: 5px; margin: 0; padding: 0 4px; line-height: 16px; font-size: 10px; }
.aura-card-follow { position: absolute; top: 5px; right: 5px; display: grid; place-items: center; width: 24px; height: 24px; padding: 0; color: #b7a98d; background: transparent; border: 0; cursor: pointer; }
.aura-card-follow.active, .detail-follow.active { color: #d45b58; }
.aura-card-name { display: block; width: 100%; margin-top: 5px; overflow: hidden; color: #4d3925; font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
.aura-card-status { display: block; width: 100%; margin-top: 2px; overflow: hidden; color: #9a8462; font-size: 10px; text-overflow: ellipsis; white-space: nowrap; }
.detail-follow { display: grid; place-items: center; width: 26px; height: 26px; padding: 0; color: #b7a98d; background: transparent; border: 0; cursor: pointer; }
.aura-card-follow >>> .ivu-icon, .detail-follow >>> .ivu-icon { font-size: 18px; }
.aura-list-empty, .aura-no-selection, .aura-empty { display: grid; place-items: center; min-height: 110px; color: #a08c6c; font-size: 12px; }
.aura-right { min-width: 0; max-height: 550px; padding: 14px 16px; overflow-y: auto; }
.aura-detail-head { display: flex; flex-direction: column; align-items: stretch; gap: 10px; padding-bottom: 12px; border-bottom: 1px solid #decfaf; text-align: left; }
.aura-basic-info { min-width: 0; width: 100%; }
.aura-name-line { display: flex; align-items: center; justify-content: flex-start; flex-wrap: wrap; gap: 6px; }
.aura-name-line h3 { margin: 0; color: #48321f; font-size: 19px; }
.aura-name-line >>> .ivu-tag { margin: 0; }
.aura-subline { margin-top: 6px; color: #8c7657; font-size: 11px; }
.aura-fact-line { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 7px; color: #9b815b; font-size: 10px; }
.aura-fact-line b { color: #624525; }
.aura-exp-line { display: grid; gap: 3px; max-width: 520px; margin-top: 8px; color: #947b55; font-size: 10px; }
.aura-exp-line >>> .ivu-progress-bg { background: #3978b7; }
.aura-action-row { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 5px; margin-top: 10px; }
.aura-action-row .ivu-btn { padding: 2px 8px; font-size: 11px; }
.farm-aura-page >>> .ivu-btn { color: #426581; background: #f7fbff; border-color: #b9cde0; box-shadow: none; }
.farm-aura-page >>> .ivu-btn:hover { color: #315a7c; background: #eef6fd; border-color: #7fa9ca; }
.farm-aura-page >>> .ivu-btn-primary,
.farm-aura-page >>> .ivu-btn-success { color: #fff8df; background: #6b8f70; border-color: #54765b; }
.farm-aura-page >>> .ivu-btn-primary:hover,
.farm-aura-page >>> .ivu-btn-success:hover { color: #fff8df; background: #5d8064; border-color: #496c50; }
.farm-aura-page >>> .ivu-btn-warning { color: #fff8df; background: #a67842; border-color: #865d31; }
.farm-aura-page >>> .ivu-btn-warning:hover { color: #fff8df; background: #906c3b; border-color: #714d29; }
.farm-aura-page >>> .ivu-btn-error { color: #fff7e3; background: #a66c4d; border-color: #875338; }
.farm-aura-page >>> .ivu-btn-error:hover { color: #fff7e3; background: #915a3e; border-color: #73432f; }
.aura-detail-tabs { margin-top: 10px; }
.aura-detail-tabs >>> .ivu-tabs-bar { margin-bottom: 10px; }
.aura-detail-tabs >>> .ivu-tabs-tab { padding: 7px 14px; color: #856a46; font-size: 12px; }
.aura-story { margin-bottom: 10px; padding: 8px 10px; color: #8d7555; background: rgba(255, 248, 229, .72); border: 1px solid #e6d7b9; font-size: 11px; line-height: 1.5; }
.aura-story span { color: #6c4b28; font-weight: 700; }
.aura-story p { margin: 4px 0 0; }
.aura-section-title { margin: 4px 0 7px; padding-left: 7px; color: #654725; border-left: 3px solid #b78a46; font-weight: 700; font-size: 12px; }
.aura-section-title--skill { margin-top: 14px; }
.aura-attributes-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 5px 6px; }
.aura-attribute { display: flex; justify-content: space-between; gap: 8px; padding: 6px 8px; color: #977b54; background: rgba(255, 248, 227, .8); border: 1px solid #eadcbd; font-size: 11px; }
.aura-attribute strong { color: #5c4327; }
.aura-skills-columns { display: grid; grid-template-columns: 1fr; gap: 8px; }
.aura-skill-column h4 { margin: 0 0 5px; color: #80633d; font-size: 11px; }
.aura-skill { display: flex; align-items: center; justify-content: space-between; width: 100%; margin-bottom: 4px; padding: 6px 8px; color: #735534; background: #fff9eb; border: 1px solid #e1d1ae; border-radius: 3px; cursor: pointer; text-align: left; }
.aura-skill.active { color: #315a7c; background: #eef6fd; border-color: #7fa9ca; }
.aura-skill em { color: #a9864d; font-size: 10px; font-style: normal; }
.aura-skill-detail { min-height: 72px; margin-top: 8px; padding: 9px 10px; color: #735638; background: rgba(255, 247, 224, .82); border: 1px solid #e0cda6; }
.aura-skill-detail > div { display: flex; align-items: center; gap: 7px; }
.aura-skill-detail p { margin: 7px 0 3px; color: #8c7658; font-size: 11px; line-height: 1.5; }
.aura-skill-detail small { color: #a08151; font-size: 10px; }
.aura-tab-toolbar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; color: #644728; font-size: 12px; }
.aura-bag-list { display: grid; gap: 6px; }
.aura-bag-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 8px 10px; background: #f7fbff; border: 1px solid #c9dced; font-size: 12px; }
.aura-enhance-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
.aura-enhance-card { display: grid; gap: 6px; padding: 9px; background: #f7fbff; border: 1px solid #c9dced; }
.aura-enhance-card.disabled { opacity: .66; }
.aura-enhance-head { display: flex; align-items: center; justify-content: space-between; gap: 5px; color: #624526; font-size: 12px; }
.aura-enhance-head >>> .ivu-tag { margin: 0; }
.aura-enhance-card p, .aura-enhance-card small { margin: 0; color: #927956; font-size: 10px; }
.aura-records { margin-top: 12px; }
.aura-records h4 { margin: 0 0 6px; color: #6c4c29; font-size: 12px; }
.aura-record-row { display: grid; grid-template-columns: 1fr auto auto; gap: 9px; padding: 6px 8px; color: #896d49; border-bottom: 1px solid #eadcc1; font-size: 11px; }
.aura-record-row strong { color: #5f4325; }
.aura-record-row small { color: #aa916f; }
.aura-awakening-info { display: grid; gap: 7px; color: #735535; font-size: 12px; }
.aura-awakening-info p { margin: 0 0 4px; line-height: 1.6; }
.aura-awakening-info div { display: flex; justify-content: space-between; padding: 5px 7px; background: #f7fbff; }
@media (max-width: 800px) {
  .aura-page-toolbar { align-items: flex-start; flex-direction: column; }
  .aura-page-actions { width: 100%; justify-content: flex-end; flex-wrap: wrap; }
  .aura-count { margin-right: auto; }
  .aura-filter-controls > .ivu-input-wrapper, .aura-filter-controls > .ivu-select { flex: 1 1 calc(50% - 6px); }
  .aura-layout { display: block; }
  .aura-left { border-right: 0; border-bottom: 1px solid #d9c9a7; }
  .aura-list { max-height: 300px; }
  .aura-right { padding: 12px; }
  .aura-attributes-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .aura-skills-columns, .aura-enhance-grid { grid-template-columns: 1fr; }
}

.farm-aura-page--blue .aura-filter-panel { background: #fff; border-color: #c9d9e8; }
.farm-aura-page--blue .aura-filter-controls >>> .ivu-input,
.farm-aura-page--blue .aura-filter-controls >>> .ivu-select-selection,
.farm-aura-page--blue .aura-filter-controls >>> .ivu-radio-group-button .ivu-radio-wrapper,
.farm-aura-page--blue .aura-filter-controls >>> .ivu-btn {
  color: #426581;
  background: #f7fbff;
  border-color: #b9cde0;
  box-shadow: none;
}
.farm-aura-page--blue .aura-filter-controls >>> .ivu-input:hover,
.farm-aura-page--blue .aura-filter-controls >>> .ivu-input:focus,
.farm-aura-page--blue .aura-filter-controls >>> .ivu-select-selection:hover,
.farm-aura-page--blue .aura-filter-controls >>> .ivu-radio-group-button .ivu-radio-wrapper:hover,
.farm-aura-page--blue .aura-filter-controls >>> .ivu-btn:hover {
  color: #315a7c;
  background: #eef6fd;
  border-color: #7fa9ca;
}
.farm-aura-page--blue .aura-filter-controls >>> .ivu-input::placeholder { color: #8298aa; }
.farm-aura-page--blue .aura-filter-controls >>> .ivu-select-arrow,
.farm-aura-page--blue .aura-filter-controls >>> .ivu-input-icon { color: #6489a8; }
.farm-aura-page--blue .aura-filter-controls >>> .ivu-radio-group-button .ivu-radio-wrapper-checked,
.farm-aura-page--blue .aura-filter-controls >>> .ivu-radio-group-button .ivu-radio-wrapper-checked:hover {
  color: #fff;
  background: #3978b7;
  border-color: #2f669d;
  box-shadow: none;
}
.farm-aura-page--blue .aura-filter-controls >>> .ivu-btn { height: 30px; padding: 0 10px; font-size: 11px; }
.farm-aura-page--blue .aura-filter-controls >>> .ivu-btn-error,
.farm-aura-page--blue .aura-filter-controls >>> .ivu-btn-error:hover {
  color: #fff;
  background: #b86d70;
  border-color: #9f565a;
}
.farm-aura-page--blue .aura-filter-controls >>> .ivu-btn-error:hover { background: #a75d61; }
</style>

<style>
.farm-aura-page--blue{color:#315a7c;background:#f5f9fc}.farm-aura-page--blue .aura-page-toolbar,.farm-aura-page--blue .aura-filter-panel{background:#fff;border-color:#c9dced}.farm-aura-page--blue .farm-panel-kicker{color:#5f8eb6}.farm-aura-page--blue .aura-page-toolbar h3{color:#315a7c}.farm-aura-page--blue .aura-page-toolbar p,.farm-aura-page--blue .aura-count{color:#6f8da6}.farm-aura-page--blue .aura-garden-content{background:#f5f9fc}.farm-aura-page--blue .aura-filter-controls >>> .ivu-input,.farm-aura-page--blue .aura-filter-controls >>> .ivu-select-selection{background:#f7fbff;border-color:#b9cde0}.farm-aura-page--blue .aura-list{background:#f5f9fc}.farm-aura-page--blue .aura-list-item{background:#fff;border-color:#c9dced}.farm-aura-page--blue .aura-list-item.active{background:#eef6fd;border-color:#7fa9ca;box-shadow:inset 3px 0 #3978b7}.farm-aura-page--blue .aura-detail{background:#fff;border-color:#c9dced}.farm-aura-page--blue .aura-name,.farm-aura-page--blue .aura-section-title{color:#315a7c}.farm-aura-page--blue .aura-description,.farm-aura-page--blue .aura-subline,.farm-aura-page--blue .aura-fact-line{color:#6f8da6}.farm-aura-page--blue .aura-section-title{border-left-color:#3978b7}.farm-aura-page--blue .aura-story{color:#54738d;background:#f7fbff;border-color:#c9dced}.farm-aura-page--blue .aura-story span{color:#315a7c}.aura-travel-modal .ivu-modal-content{background:#f5f9fc;border:1px solid #b9cde0}.aura-travel-modal .ivu-modal-header{background:#fff;border-bottom-color:#c9dced}.aura-travel-modal .ivu-modal-header-inner{color:#315a7c}.aura-travel-modal .ivu-modal-body{background:#f5f9fc}
.aura-travel-modal .ivu-modal-header{display:none}.aura-travel-modal .ivu-modal-body{max-height:94vh;padding-top:10px}
.cave-aura-modal .ivu-modal-content{background:#f5f9fc!important;border:1px solid #b9cde0!important;border-radius:6px!important;box-shadow:0 16px 42px rgba(49,90,124,.2)!important}.cave-aura-modal .ivu-modal-header{background:#fff!important;border-bottom:1px solid #c9dced!important}.cave-aura-modal .ivu-modal-body{padding:0!important;background:#f5f9fc!important}.cave-aura-modal .ivu-modal-footer{background:#fff!important;border-top:1px solid #c9dced!important}.cave-aura-modal .ivu-modal-close .ivu-icon{color:#5f8eb6!important}.cave-aura-modal .ivu-modal-close .ivu-icon:hover{color:#315a7c!important}
</style>

<style>
.aura-travel-modal .ivu-modal {
  width: min(1200px, calc(100vw - 24px)) !important;
  max-width: none;
  margin: 0 auto;
  top: 3vh;
}
.aura-travel-modal .ivu-modal-content {
  max-height: 94vh;
  overflow: hidden;
  border-radius: 6px;
  background: #f5f9fc;
}
.aura-travel-modal .ivu-modal-header {
  padding: 12px 16px;
  border-bottom: 1px solid #c9dced;
  background: #ffffff;
}
.aura-travel-modal .ivu-modal-header-inner {
  color: #654a2e;
  font: 900 18px/1.2 "KaiTi", "STKaiti", serif;
}
.aura-travel-modal .ivu-modal-body {
  max-height: calc(94vh - 48px);
  padding: 10px;
  overflow-x: hidden;
  overflow-y: auto;
}
.aura-travel-modal .farm-travel-mode {
  min-width: 0;
}
@media (max-width: 720px) {
  .aura-travel-modal .ivu-modal {
    width: 100vw !important;
    top: 0;
  }
  .aura-travel-modal .ivu-modal-content {
    max-height: 100vh;
    border-radius: 0;
  }
  .aura-travel-modal .ivu-modal-body {
    max-height: calc(100vh - 48px);
    padding: 6px;
  }
}

/* Keep the detail panel on the same white-and-blue visual system as the cave modal. */
.farm-aura-page--blue .aura-right,
.farm-aura-page--blue .aura-detail {
  color: #315a7c !important;
  background: #ffffff !important;
  border-color: #c9dced !important;
}
.farm-aura-page--blue .aura-detail-head {
  border-bottom-color: #c9dced !important;
}
.farm-aura-page--blue .aura-name-line h3,
.farm-aura-page--blue .aura-fact-line b,
.farm-aura-page--blue .aura-attribute strong,
.farm-aura-page--blue .aura-enhance-head,
.farm-aura-page--blue .aura-record-row strong,
.farm-aura-page--blue .aura-awakening-info {
  color: #315a7c !important;
}
.farm-aura-page--blue .aura-subline,
.farm-aura-page--blue .aura-fact-line,
.farm-aura-page--blue .aura-exp-line,
.farm-aura-page--blue .aura-tab-toolbar,
.farm-aura-page--blue .aura-empty,
.farm-aura-page--blue .aura-records h4 {
  color: #6f8da6 !important;
}
.farm-aura-page--blue .aura-story,
.farm-aura-page--blue .aura-skill-detail,
.farm-aura-page--blue .aura-attribute,
.farm-aura-page--blue .aura-bag-row,
.farm-aura-page--blue .aura-enhance-card,
.farm-aura-page--blue .aura-awakening-info div {
  color: #54738d !important;
  background: #f7fbff !important;
  border-color: #c9dced !important;
}
.farm-aura-page--blue .aura-story span,
.farm-aura-page--blue .aura-section-title,
.farm-aura-page--blue .aura-skill-column h4 {
  color: #315a7c !important;
}
.farm-aura-page--blue .aura-section-title {
  border-left-color: #3978b7 !important;
}
.farm-aura-page--blue .aura-skill {
  color: #426581 !important;
  background: #ffffff !important;
  border-color: #c9dced !important;
}
.farm-aura-page--blue .aura-skill:hover,
.farm-aura-page--blue .aura-skill.active {
  color: #315a7c !important;
  background: #eef6fd !important;
  border-color: #7fa9ca !important;
}
.farm-aura-page--blue .aura-skill em,
.farm-aura-page--blue .aura-skill-detail p,
.farm-aura-page--blue .aura-skill-detail small,
.farm-aura-page--blue .aura-enhance-card p,
.farm-aura-page--blue .aura-enhance-card small,
.farm-aura-page--blue .aura-record-row,
.farm-aura-page--blue .aura-record-row small {
  color: #6f8da6 !important;
}
.farm-aura-page--blue .aura-record-row {
  border-bottom-color: #dbe8f2 !important;
}
.farm-aura-page--blue .aura-detail .ivu-btn {
  color: #426581 !important;
  background: #f7fbff !important;
  border-color: #b9cde0 !important;
  box-shadow: none !important;
}
.farm-aura-page--blue .aura-detail .ivu-btn:hover,
.farm-aura-page--blue .aura-detail .ivu-btn:focus {
  color: #315a7c !important;
  background: #eef6fd !important;
  border-color: #7fa9ca !important;
}
.farm-aura-page--blue .aura-detail .ivu-btn-primary,
.farm-aura-page--blue .aura-detail .ivu-btn-success {
  color: #ffffff !important;
  background: #3978b7 !important;
  border-color: #2f669d !important;
}
.farm-aura-page--blue .aura-detail .ivu-btn-primary:hover,
.farm-aura-page--blue .aura-detail .ivu-btn-success:hover {
  color: #ffffff !important;
  background: #2f669d !important;
  border-color: #285a8d !important;
}
.farm-aura-page--blue .aura-detail .ivu-btn-warning {
  color: #ffffff !important;
  background: #5f96bf !important;
  border-color: #4c80a8 !important;
}
.farm-aura-page--blue .aura-detail .ivu-btn-error {
  color: #ffffff !important;
  background: #b86d70 !important;
  border-color: #9f565a !important;
}
.farm-aura-page--blue .aura-detail-tabs .ivu-tabs-tab {
  color: #6f8da6 !important;
}
.farm-aura-page--blue .aura-detail-tabs .ivu-tabs-tab:hover,
.farm-aura-page--blue .aura-detail-tabs .ivu-tabs-tab-active {
  color: #3978b7 !important;
}
.farm-aura-page--blue .aura-detail-tabs .ivu-tabs-ink-bar {
  background: #3978b7 !important;
}
.farm-aura-page--blue .aura-detail .ivu-progress-inner {
  background: #e5eff7 !important;
}
.farm-aura-page--blue .aura-detail .ivu-progress-bg {
  background: #3978b7 !important;
}
.aura-travel-modal .ivu-modal-content,
.aura-travel-modal .ivu-modal-body {
  background: #f5f9fc !important;
  border-color: #b9cde0 !important;
}
.aura-travel-modal .ivu-modal-header {
  background: #ffffff !important;
  border-bottom-color: #c9dced !important;
}
.farm-aura-page--blue .aura-page-toolbar,
.farm-aura-page--blue .aura-filter-panel,
.farm-aura-page--blue .aura-layout,
.farm-aura-page--blue .aura-left,
.farm-aura-page--blue .aura-list,
.farm-aura-page--blue .aura-garden-content {
  color: #315a7c !important;
  background: #f5f9fc !important;
  border-color: #c9dced !important;
}
.farm-aura-page--blue .aura-page-toolbar,
.farm-aura-page--blue .aura-filter-panel {
  background: #ffffff !important;
}
.farm-aura-page--blue .aura-filter-controls .ivu-input,
.farm-aura-page--blue .aura-filter-controls .ivu-select-selection,
.farm-aura-page--blue .aura-filter-controls .ivu-radio-wrapper,
.farm-aura-page--blue .aura-filter-controls .ivu-btn {
  color: #426581 !important;
  background: #f7fbff !important;
  border-color: #b9cde0 !important;
}
.farm-aura-page--blue .aura-filter-controls .ivu-radio-wrapper-checked,
.farm-aura-page--blue .aura-filter-controls .ivu-btn-primary {
  color: #ffffff !important;
  background: #3978b7 !important;
  border-color: #2f669d !important;
}
.farm-aura-page--blue .aura-list-item {
  color: #426581 !important;
  background: #ffffff !important;
  border-color: #c9dced !important;
}
.farm-aura-page--blue .aura-list-item:hover,
.farm-aura-page--blue .aura-list-item.active {
  color: #315a7c !important;
  background: #eef6fd !important;
  border-color: #7fa9ca !important;
}
.farm-aura-page--blue .aura-list-icon {
  background: #eaf3f9 !important;
  border-color: #9fc1d9 !important;
  box-shadow: 0 2px 7px rgba(66, 101, 129, .16), inset 0 0 0 2px rgba(255, 255, 255, .7) !important;
}
.farm-aura-page--blue .aura-card-name,
.farm-aura-page--blue .aura-name-line h3 {
  color: #315a7c !important;
}
.farm-aura-page--blue .aura-card-status,
.farm-aura-page--blue .aura-list-empty,
.farm-aura-page--blue .aura-no-selection {
  color: #6f8da6 !important;
}
.farm-aura-page--blue .aura-card-grade {
  color: #3978b7 !important;
  background: #eef6fd !important;
  border-color: #b9cde0 !important;
}
.farm-aura-page--blue .aura-list,
.farm-aura-page--blue .aura-right {
  scrollbar-color: #7fa9ca #eaf3f9 !important;
}
.farm-aura-page--blue .aura-list::-webkit-scrollbar-track,
.farm-aura-page--blue .aura-right::-webkit-scrollbar-track {
  background: #eaf3f9 !important;
}
.farm-aura-page--blue .aura-list::-webkit-scrollbar-thumb,
.farm-aura-page--blue .aura-right::-webkit-scrollbar-thumb {
  background: #7fa9ca !important;
  border-color: #eaf3f9 !important;
}
</style>



// WEBPACK FOOTER //
// src/components/cave-farm/CaveFarmAura.vue